import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import moment from 'moment-timezone';
import CommonHeader from '../../../util_components/common_header';
import { get_api, post_api, put_api } from '../../../../redux/api_funcs';
import { get_api_url } from '../../../../utils/urls';
import { day_format, time_format } from '../../../../constants';
import { fmt_amount, status_badge } from './helpers';
import {
  can_show_resolve_panel,
  classify_execute_response,
  credits_result_text,
  credits_text,
  entitlement_warning_text,
  flatten_session_items,
  loose_active_warning_text,
  membership_result_text,
  payments_warning_text,
  repeat_slot_text,
  resolve_button_label,
  sessions_result_text,
  steps_progress,
  summarize_preview_for_confirm
} from './resolve_helpers';

// OQ-6 evidence field set (text + file). File fields hold a Stripe file id from the upload endpoint.
const EVIDENCE_TEXT = [
  ['product_description', 'Product description'],
  ['customer_name', 'Customer name'],
  ['customer_email_address', 'Customer email'],
  ['billing_address', 'Billing address'],
  ['access_activity_log', 'Access / activity log'],
  ['cancellation_policy_disclosure', 'Cancellation policy disclosure'],
  ['uncategorized_text', 'Other (uncategorized)']
];
const EVIDENCE_FILES = [
  ['receipt', 'Receipt'],
  ['service_documentation', 'Service documentation'],
  ['customer_communication', 'Customer communication'],
  ['cancellation_policy', 'Cancellation policy']
];
const INTERNAL_STATUS_OPTIONS = [
  'NEW',
  'REVIEWING',
  'EVIDENCE_SUBMITTED',
  'ACCEPTED',
  'WONT_CONTEST',
  'CLOSED'
];

// Static tone → token map (grep-able; dynamic var(--state-${tone}-*) hides token usage).
const TONE_STYLES = {
  info: { background: 'var(--state-info-bg)', color: 'var(--state-info-text)' },
  success: { background: 'var(--state-success-bg)', color: 'var(--state-success-text)' },
  warning: { background: 'var(--state-warning-bg)', color: 'var(--state-warning-text)' },
  error: { background: 'var(--state-error-bg)', color: 'var(--state-error-text)' }
};

const input_style = {
  width: '100%',
  background: 'var(--input-bg)',
  color: 'var(--input-text)',
  border: '1px solid var(--input-border)',
  borderRadius: '4px',
  padding: '4px 6px'
};

class ChargebackDetails extends Component {
  state = {
    loading: true,
    item: null,
    history: [],
    evidence: {},
    notes: '',
    internal_status: '',
    accept_armed: false,
    saving: false,
    msg: '',
    // resolve-lost panel (PROD-7808 T11)
    resolve_open: false,
    resolve_loading: false,
    preview: null,
    resolve_msg: '',
    resolve_msg_tone: 'info',
    resolve_confirm: false,
    executing: false,
    exec_result: null,
    retain_reason: '',
    retain_armed: false,
    retain_saving: false,
    retain_done: false
  };

  apply_btn_ref = React.createRef();

  confirm_btn_ref = React.createRef();

  componentDidMount() {
    this.load_data();
  }

  get_id = () => new URLSearchParams(this.props.location.search).get('id') || '';

  load_data = () => {
    const id = this.get_id();
    if (!id) {
      this.setState({ loading: false });
      return;
    }
    this.setState({ loading: true }, () => {
      const base = `/v2/cms_user/chargebacks/${encodeURIComponent(id)}`;
      const detail_url = get_api_url(base, false, true);
      const history_url = get_api_url(`${base}/history`, false, true);
      Promise.all([get_api(detail_url, true), get_api(history_url, true)])
        .then(([d, h]) => {
          // get_api resolves the raw axios response → the goserver payload is at res.data.data.
          const item = (d && d.data && d.data.data) || null;
          const hist = (h && h.data && h.data.data) || [];
          this.setState({
            loading: false,
            item,
            history: Array.isArray(hist) ? hist : [],
            // prefill the evidence text fields we already know (OQ-6 auto-prefill).
            evidence: item
              ? {
                  product_description: 'MyYogaTeacher membership',
                  customer_name: item.student_name || '',
                  customer_email_address: item.student_email || ''
                }
              : {},
            notes: (item && item.internal_notes) || '',
            internal_status: (item && item.internal_status) || ''
          });
        })
        .catch(() => this.setState({ loading: false }));
    });
  };

  fmt_dt = (v) =>
    v ? moment(v).tz(this.props.iana_timezone).format(`${day_format} ${time_format}`) : '--';

  // Weekly repeat slots have no date — only the weekday+time of repeat_time matters.
  fmt_slot = (v) => (v ? moment(v).tz(this.props.iana_timezone).format('dddd h:mm A z') : '--');

  is_super_user = () => this.props.access_type === 'SUPER_USER';

  // err_msg surfaces the goserver reason from a rejected axios error.
  err_msg = (err, fallback) => {
    const reason =
      err &&
      err.response &&
      err.response.data &&
      (err.response.data.reason || err.response.data.message);
    return reason ? `${fallback}: ${reason}` : fallback;
  };

  set_evidence_field = (field, value) =>
    this.setState((prev) => ({ evidence: { ...prev.evidence, [field]: value } }));

  upload_file = (field, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    fd.append('field', field);
    const url = get_api_url(
      `/v2/cms_user/chargebacks/${encodeURIComponent(this.get_id())}/evidence/upload`,
      false,
      true
    );
    this.setState({ saving: true, msg: '' });
    post_api(url, fd, true, 'formdata')
      .then((res) => {
        const payload = (res && res.data && res.data.data) || {};
        this.set_evidence_field(field, payload.file_id || '');
        this.setState({ saving: false, msg: `Uploaded ${field}` });
      })
      .catch((err) =>
        this.setState({ saving: false, msg: this.err_msg(err, `Upload failed for ${field}`) })
      );
  };

  submit_evidence = () => {
    const url = get_api_url(
      `/v2/cms_user/chargebacks/${encodeURIComponent(this.get_id())}/evidence/submit`,
      false,
      true
    );
    this.setState({ saving: true, msg: '' });
    post_api(url, this.state.evidence, true)
      .then(() =>
        this.setState({ saving: false, msg: 'Evidence submitted to Stripe' }, this.load_data)
      )
      .catch((err) => this.setState({ saving: false, msg: this.err_msg(err, 'Submit failed') }));
  };

  accept_dispute = () => {
    if (!this.state.accept_armed) {
      this.setState({
        accept_armed: true,
        msg: 'Click Accept again to confirm conceding this chargeback.'
      });
      return;
    }
    const url = get_api_url(
      `/v2/cms_user/chargebacks/${encodeURIComponent(this.get_id())}/accept`,
      false,
      true
    );
    this.setState({ saving: true, msg: '', accept_armed: false });
    post_api(url, {}, true)
      .then(() =>
        this.setState({ saving: false, msg: 'Dispute accepted (conceded)' }, this.load_data)
      )
      .catch((err) => this.setState({ saving: false, msg: this.err_msg(err, 'Accept failed') }));
  };

  save_notes = () => {
    const url = get_api_url(
      `/v2/cms_user/chargebacks/${encodeURIComponent(this.get_id())}/notes`,
      false,
      true
    );
    this.setState({ saving: true, msg: '' });
    put_api(
      url,
      { internal_notes: this.state.notes, internal_status: this.state.internal_status },
      true
    )
      .then(() => this.setState({ saving: false, msg: 'Notes saved' }, this.load_data))
      .catch((err) => this.setState({ saving: false, msg: this.err_msg(err, 'Save failed') }));
  };

  resolve_base = () => `/v2/cms_user/chargebacks/${encodeURIComponent(this.get_id())}`;

  toggle_resolve = () => {
    this.setState(
      (prev) => ({ resolve_open: !prev.resolve_open }),
      () => {
        const { resolve_open, preview, resolve_loading } = this.state;
        if (resolve_open && !preview && !resolve_loading) this.fetch_preview();
      }
    );
  };

  fetch_preview = (msg = '') => {
    // H-M5: monotonic request id — an out-of-order response must not install a stale preview_token.
    this.preview_req_id = (this.preview_req_id || 0) + 1;
    const req_id = this.preview_req_id;
    const url = get_api_url(`${this.resolve_base()}/resolve-lost/preview`, false, true);
    // Clear exec_result (same as retry_resolve) — the render gate hides fresh previews behind it.
    this.setState({
      resolve_loading: true,
      resolve_msg: msg,
      resolve_msg_tone: 'info',
      resolve_confirm: false,
      exec_result: null
    });
    get_api(url, true)
      .then((res) => {
        if (req_id !== this.preview_req_id) return; // stale — a newer preview request owns state
        this.setState({
          resolve_loading: false,
          preview: (res && res.data && res.data.data) || null
        });
      })
      .catch((err) => {
        if (req_id !== this.preview_req_id) return;
        this.setState({
          resolve_loading: false,
          preview: null,
          resolve_msg: this.err_msg(err, 'Preview failed'),
          resolve_msg_tone: 'error'
        });
      });
  };

  execute_resolve = () => {
    const { preview } = this.state;
    if (!preview || !preview.preview_token) {
      this.setState({
        resolve_msg: 'No preview loaded — refresh the preview first.',
        resolve_msg_tone: 'error'
      });
      return;
    }
    const url = get_api_url(`${this.resolve_base()}/resolve-lost`, false, true);
    this.setState({ executing: true, resolve_msg: '' });
    post_api(url, { preview_token: preview.preview_token }, true)
      .then((res) => {
        const data = (res && res.data && res.data.data) || {};
        const cls = classify_execute_response(data);
        this.setState({ executing: false, resolve_confirm: false, exec_result: data }, () => {
          // fully resolved → refresh the detail (internal_status/history changed).
          if (cls.kind === 'resolved') this.load_data();
        });
      })
      .catch((err) => {
        if (err && err.response && err.response.status === 409) {
          // A3 stale token — surface the server's reason (H-M3) then re-preview for re-confirm.
          const reason =
            (err.response.data && (err.response.data.reason || err.response.data.message)) ||
            'State changed';
          this.setState({ executing: false, exec_result: null }, () =>
            this.fetch_preview(
              `${reason} — re-fetched preview. Review the new plan and confirm again.`
            )
          );
          return;
        }
        this.setState({
          executing: false,
          resolve_confirm: false,
          resolve_msg: this.err_msg(err, 'Resolve failed'),
          resolve_msg_tone: 'error'
        });
      });
  };

  // Any retry/resume path must go through a FRESH preview (fresh token) per contract.
  retry_resolve = () => {
    this.setState({ exec_result: null }, () =>
      this.fetch_preview('Preview refreshed — review and confirm again to resume.')
    );
  };

  close_no_action = () => {
    const reason = this.state.retain_reason.trim();
    if (!reason) {
      this.setState({
        resolve_msg: 'A reason is required to close with no action.',
        resolve_msg_tone: 'error'
      });
      return;
    }
    if (!this.state.retain_armed) {
      this.setState({
        retain_armed: true,
        resolve_msg:
          'Click "Confirm close — no action" to close this dispute with NO account changes.',
        resolve_msg_tone: 'info'
      });
      return;
    }
    const url = get_api_url(`${this.resolve_base()}/close-no-action`, false, true);
    this.setState({ retain_saving: true, retain_armed: false, resolve_msg: '' });
    post_api(url, { reason }, true)
      .then(() =>
        // retain_done keeps the panel mounted so the confirmation survives the refresh
        // (load_data flips internal_status to NO_ACTION, which would unmount it).
        this.setState(
          {
            retain_saving: false,
            retain_done: true,
            preview: null,
            resolve_confirm: false,
            resolve_msg: 'Closed as no-action (retained).',
            resolve_msg_tone: 'success'
          },
          this.load_data
        )
      )
      .catch((err) => {
        // 409 half-executed → surface the server reason verbatim (contract T9).
        const verbatim = err && err.response && err.response.data && err.response.data.reason;
        this.setState({
          retain_saving: false,
          resolve_msg: verbatim || this.err_msg(err, 'Close failed'),
          resolve_msg_tone: 'error'
        });
      });
  };

  // history_detail flattens an audit row's detail JSON (notes, status, evidence fields, file) to one line.
  history_detail = (h) => {
    const d = h.detail;
    if (!d || typeof d !== 'object') return '';
    const parts = [];
    if (d.notes) parts.push(`"${d.notes}"`);
    if (d.internal_status) parts.push(`status: ${d.internal_status}`);
    if (Array.isArray(d.fields) && d.fields.length) parts.push(`fields: ${d.fields.join(', ')}`);
    if (d.filename) parts.push(`file: ${d.filename}`);
    return parts.join(' · ');
  };

  field = (label, value) => (
    <div
      className="flex"
      style={{ padding: '4px 0', borderBottom: '1px solid var(--border-default)' }}
    >
      <div style={{ width: '180px', color: 'var(--text-muted)' }}>{label}</div>
      <div style={{ flex: 1, color: 'var(--text-primary)' }}>{value}</div>
    </div>
  );

  render_detail = (item) => {
    const badge = status_badge(item.status);
    return (
      <div
        style={{
          background: 'var(--surface-card)',
          border: '1px solid var(--border-default)',
          borderRadius: '8px',
          padding: '16px',
          maxWidth: '760px'
        }}
      >
        {this.field(
          'Kind',
          item.kind === 'early_fraud_warning'
            ? 'Early Fraud Warning (near chargeback)'
            : 'Chargeback'
        )}
        {this.field(
          'Status',
          <span style={{ ...badge, padding: '2px 8px', borderRadius: '10px', fontWeight: 600 }}>
            {item.status || '--'}
          </span>
        )}
        {item.match_status && item.match_status !== 'matched'
          ? this.field(
              'Match',
              <span style={{ color: 'var(--state-warning-text)' }}>
                ⚠ {item.match_status} — needs manual investigation
              </span>
            )
          : null}
        {this.field('Amount', fmt_amount(item.amount_minor, item.currency))}
        {this.field('Reason', item.reason || '--')}
        {this.field(
          'Student',
          item.student_uuid ? (
            <a
              className="link-no-dec"
              href={`/studentprofile?id=${item.student_uuid}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'var(--brand-primary)' }}
            >
              {item.student_name || item.student_uuid}
              {item.student_email ? ` · ${item.student_email}` : ''}
            </a>
          ) : (
            '--'
          )
        )}
        {this.field('Charge', item.charge_id || '--')}
        {this.field('Payment Intent', item.payment_intent_id || '--')}
        {this.field('Evidence due by', this.fmt_dt(item.evidence_due_by))}
        {this.field('Internal status', item.internal_status || '--')}
        {this.field('Handled by', item.handled_by || '--')}
        {this.field('Created (Stripe)', this.fmt_dt(item.stripe_created_date))}
      </div>
    );
  };

  render_lifecycle = (item) => {
    // Lifecycle is SUPER_USER-only and applies to formal disputes (EFWs have no Stripe lifecycle).
    if (!this.is_super_user() || item.kind !== 'dispute') return null;
    const { evidence, saving, msg, notes, internal_status, accept_armed } = this.state;
    return (
      <div style={{ marginTop: '20px', maxWidth: '760px' }}>
        <div style={{ fontWeight: 600, marginBottom: '8px', color: 'var(--text-primary)' }}>
          Actions (SUPER_USER)
        </div>
        {msg ? (
          <div
            className="ct-vsm-font"
            style={{ color: 'var(--state-info-text)', marginBottom: '8px' }}
          >
            {msg}
          </div>
        ) : null}

        <div style={{ fontWeight: 600, color: 'var(--text-primary)', margin: '8px 0 4px' }}>
          Evidence
        </div>
        {EVIDENCE_TEXT.map(([key, label]) => (
          <div key={key} style={{ marginBottom: '6px' }}>
            <div className="ct-vsm-font" style={{ color: 'var(--text-muted)' }}>
              {label}
            </div>
            <textarea
              rows={key === 'access_activity_log' || key === 'uncategorized_text' ? 2 : 1}
              style={input_style}
              value={evidence[key] || ''}
              onChange={(e) => this.set_evidence_field(key, e.target.value)}
            />
          </div>
        ))}
        {EVIDENCE_FILES.map(([key, label]) => (
          <div key={key} style={{ marginBottom: '6px' }}>
            <div className="ct-vsm-font" style={{ color: 'var(--text-muted)' }}>
              {label}
              {evidence[key] ? (
                <span style={{ color: 'var(--state-success-text)' }}>
                  {' '}
                  · uploaded ({evidence[key]})
                </span>
              ) : null}
            </div>
            <input
              type="file"
              accept="application/pdf,image/jpeg,image/png"
              onChange={(e) => this.upload_file(key, e.target.files && e.target.files[0])}
            />
          </div>
        ))}
        <button
          type="button"
          className="ct-small-btn"
          disabled={saving}
          onClick={this.submit_evidence}
          style={{
            marginTop: '6px',
            background: 'var(--brand-primary)',
            color: 'var(--text-inverse)'
          }}
        >
          Submit evidence
        </button>
        <button
          type="button"
          className="ct-small-btn"
          disabled={saving}
          onClick={this.accept_dispute}
          style={{
            marginTop: '6px',
            marginLeft: '8px',
            background: accept_armed ? 'var(--state-error-bg)' : 'var(--surface-card)',
            color: accept_armed ? 'var(--state-error-text)' : 'var(--text-primary)',
            border: '1px solid var(--border-default)'
          }}
        >
          {accept_armed ? 'Confirm accept (concede)' : 'Accept (concede)'}
        </button>

        <div style={{ fontWeight: 600, color: 'var(--text-primary)', margin: '14px 0 4px' }}>
          Notes / status
        </div>
        <textarea
          rows={3}
          style={input_style}
          value={notes}
          onChange={(e) => this.setState({ notes: e.target.value })}
          placeholder="Internal notes"
        />
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '6px' }}>
          <select
            style={{ ...input_style, width: '220px' }}
            value={internal_status}
            onChange={(e) => this.setState({ internal_status: e.target.value })}
          >
            <option value="">(internal status)</option>
            {INTERNAL_STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="ct-small-btn"
            disabled={saving}
            onClick={this.save_notes}
          >
            Save notes
          </button>
        </div>
      </div>
    );
  };

  banner = (text, tone) => {
    const urgent = tone === 'error' || tone === 'warning';
    return (
      <div
        role={urgent ? 'alert' : undefined}
        aria-live={urgent ? undefined : 'polite'}
        style={{
          ...(TONE_STYLES[tone] || TONE_STYLES.info),
          borderRadius: '6px',
          padding: '8px 10px',
          fontWeight: 600,
          marginBottom: '8px'
        }}
      >
        {text}
      </div>
    );
  };

  sub_heading = (text) => (
    <div style={{ fontWeight: 600, color: 'var(--text-primary)', margin: '10px 0 4px' }}>
      {text}
    </div>
  );

  render_preview = (preview) => {
    const { resolve_confirm, executing } = this.state;
    const eligible =
      preview.eligibility &&
      preview.eligibility.eligible &&
      !(preview.eligibility.reasons || []).length;
    const reasons = (preview.eligibility && preview.eligibility.reasons) || [];
    const m = preview.membership || {};
    const one_on_one = (preview.sessions && preview.sessions.one_on_one) || [];
    const gc_count = ((preview.sessions && preview.sessions.gc_registrations) || []).length;
    const one_off_gc_count = ((preview.sessions && preview.sessions.one_off_gc) || []).length;
    const repeats = (preview.sessions && preview.sessions.repeat_one_on_one) || [];
    const pay_warning = payments_warning_text(preview.payments_since_dispute, this.fmt_dt);
    const entitle_warning = entitlement_warning_text(m, this.fmt_dt);
    const loose_warning = loose_active_warning_text(m);
    const progress = steps_progress(preview.steps);
    return (
      <div>
        {pay_warning ? this.banner(pay_warning, 'error') : null}
        {!eligible ? (
          <div style={{ marginBottom: '8px' }}>
            {this.banner('Not eligible for resolve', 'error')}
            <ul style={{ color: 'var(--state-error-text)', margin: '4px 0 0 18px' }}>
              {reasons.map((r) => (
                <li key={r}>{r}</li>
              ))}
            </ul>
          </div>
        ) : null}

        {this.sub_heading('Membership')}
        {entitle_warning ? this.banner(entitle_warning, 'warning') : null}
        {loose_warning ? this.banner(loose_warning, 'warning') : null}
        {m.already_cancelled && !m.entitled_until && !m.loose_active_row ? (
          <div style={{ color: 'var(--text-primary)' }}>Already cancelled — nothing to revoke.</div>
        ) : null}
        {!m.already_cancelled ? (
          <div style={{ color: 'var(--text-primary)' }}>
            {`Txn #${m.transaction_id ?? '--'} · ${m.subscription_status || '--'} · next billing ${this.fmt_dt(m.next_billing_date)} · expires ${this.fmt_dt(m.subscription_expiry_date)}`}
          </div>
        ) : null}

        {this.sub_heading('Sessions')}
        {one_on_one.length === 0 ? (
          <div style={{ color: 'var(--text-muted)' }}>No upcoming 1:1 sessions.</div>
        ) : (
          one_on_one.map((s) => (
            <div key={s.uuid || s.id} style={{ color: 'var(--text-primary)' }}>
              {`1:1 · ${this.fmt_dt(s.start_time)} · ${s.duration || '--'} min`}
            </div>
          ))
        )}
        <div style={{ color: 'var(--text-primary)' }}>{`GC registrations: ${gc_count}`}</div>
        <div style={{ color: 'var(--text-primary)' }}>
          {`One-off group classes: ${one_off_gc_count}`}
        </div>
        {repeats.length > 0 ? (
          <div>
            <div style={{ color: 'var(--text-primary)' }}>
              {`Weekly repeat slots: ${repeats.length} (will be deactivated + future sessions cancelled)`}
            </div>
            {repeats.map((r) => (
              <div
                key={r.uuid || r.id}
                className="ct-vsm-font"
                style={{ color: 'var(--text-muted)' }}
              >
                {repeat_slot_text(r, this.fmt_slot)}
              </div>
            ))}
          </div>
        ) : null}

        {this.sub_heading('Credits')}
        <div style={{ color: 'var(--text-primary)' }}>{credits_text(preview.credits)}</div>
        {preview.credits && preview.credits.note ? (
          <div className="ct-vsm-font" style={{ color: 'var(--text-muted)' }}>
            {preview.credits.note}
          </div>
        ) : null}

        {progress.length > 0 ? (
          <>
            {this.sub_heading('Step progress (previous run)')}
            {progress.map((p) => (
              <div key={p.step} style={{ color: 'var(--text-primary)' }}>
                {`${p.step}: ${p.state}`}
              </div>
            ))}
          </>
        ) : null}

        {eligible && !resolve_confirm ? (
          <button
            type="button"
            ref={this.apply_btn_ref}
            className="ct-small-btn"
            disabled={executing}
            onClick={() =>
              this.setState({ resolve_confirm: true, resolve_msg: '' }, () => {
                // move focus to the armed Confirm button (keyboard/AT flow).
                if (this.confirm_btn_ref.current) this.confirm_btn_ref.current.focus();
              })
            }
            style={{
              marginTop: '10px',
              background: 'var(--state-error-bg)',
              color: 'var(--state-error-text)',
              border: '1px solid var(--border-default)'
            }}
          >
            {resolve_button_label(preview.steps)}
          </button>
        ) : null}
        {eligible && resolve_confirm ? (
          <div
            style={{
              marginTop: '10px',
              border: '1px solid var(--state-error-text)',
              borderRadius: '6px',
              padding: '10px'
            }}
          >
            <div style={{ fontWeight: 600, color: 'var(--state-error-text)', marginBottom: '6px' }}>
              Confirm — this will execute the following on the student account:
            </div>
            {summarize_preview_for_confirm(preview, this.fmt_dt).map((line) => (
              <div key={line} style={{ color: 'var(--text-primary)' }}>
                {line}
              </div>
            ))}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
              <button
                type="button"
                ref={this.confirm_btn_ref}
                className="ct-small-btn"
                disabled={executing}
                onClick={this.execute_resolve}
                style={{ background: 'var(--state-error-bg)', color: 'var(--state-error-text)' }}
              >
                {executing ? 'Executing…' : 'Confirm — execute resolve'}
              </button>
              <button
                type="button"
                className="ct-small-btn"
                disabled={executing}
                onClick={() =>
                  this.setState({ resolve_confirm: false }, () => {
                    if (this.apply_btn_ref.current) this.apply_btn_ref.current.focus();
                  })
                }
              >
                Back
              </button>
            </div>
          </div>
        ) : null}
      </div>
    );
  };

  render_exec_result = (result) => {
    const cls = classify_execute_response(result);
    const r = result.results || {};
    const mem = r.membership || {};
    const ses = r.sessions || {};
    const cred = r.credits || {};
    const items = flatten_session_items(ses.items);
    return (
      <div style={{ marginTop: '10px' }}>
        {cls.kind === 'resolved'
          ? this.banner('Resolve complete — dispute marked RESOLVED.', 'success')
          : null}
        {cls.kind === 'aborted'
          ? this.banner(
              `${cls.reason} — refresh the preview and retry (safe after ~10 minutes).`,
              'warning'
            )
          : null}
        {cls.kind === 'partial'
          ? this.banner('Resolve did not complete — see step results below.', 'warning')
          : null}
        {cls.kind === 'error'
          ? this.banner('Unexpected response — refresh and check the dispute status.', 'error')
          : null}

        {this.sub_heading('Step results')}
        {result.steps
          ? ['membership', 'sessions', 'credits'].map((k) => (
              <div key={k} style={{ color: 'var(--text-primary)' }}>
                {`${k}: ${result.steps[k] || '--'}`}
              </div>
            ))
          : null}
        {/* H-M1: gate on the step object, not items/branch — a step that failed early still reports. */}
        {r.membership ? (
          <div style={{ color: 'var(--text-primary)', marginTop: '4px' }}>
            {membership_result_text(mem)}
          </div>
        ) : null}
        {r.sessions ? (
          <div style={{ marginTop: '4px' }}>
            <div style={{ color: 'var(--text-primary)' }}>{sessions_result_text(ses)}</div>
            {items.map((it) => (
              <div
                key={it.key}
                className="ct-vsm-font"
                style={{
                  color: it.outcome === 'failed' ? 'var(--state-error-text)' : 'var(--text-muted)'
                }}
              >
                {`${it.kind} ${it.key}: ${it.outcome}${it.detail ? ` — ${it.detail}` : ''}`}
              </div>
            ))}
          </div>
        ) : null}
        {r.credits ? (
          <div style={{ color: 'var(--text-primary)', marginTop: '4px' }}>
            {credits_result_text(cred)}
          </div>
        ) : null}

        {cls.failures.length > 0 ? (
          <>
            {this.sub_heading('Failures')}
            {cls.failures.map((f) => (
              <div key={f} style={{ color: 'var(--state-error-text)' }}>
                {f}
              </div>
            ))}
          </>
        ) : null}

        {cls.kind !== 'resolved' ? (
          <button
            type="button"
            className="ct-small-btn"
            style={{ marginTop: '8px' }}
            onClick={this.retry_resolve}
          >
            Refresh preview & retry
          </button>
        ) : null}
      </div>
    );
  };

  render_retain = () => {
    const { retain_reason, retain_armed, retain_saving, executing } = this.state;
    return (
      <div
        style={{
          marginTop: '14px',
          borderTop: '1px solid var(--border-default)',
          paddingTop: '10px'
        }}
      >
        {this.sub_heading('Close — no action (retain)')}
        <div className="ct-vsm-font" style={{ color: 'var(--text-muted)', marginBottom: '4px' }}>
          Closes the loop without touching the account. Reason is required.
        </div>
        <textarea
          rows={2}
          style={input_style}
          value={retain_reason}
          aria-label="Reason for closing with no action"
          placeholder="Why are we retaining this customer / taking no action?"
          onChange={(e) => this.setState({ retain_reason: e.target.value, retain_armed: false })}
        />
        <button
          type="button"
          className="ct-small-btn"
          disabled={retain_saving || executing || !retain_reason.trim()}
          onClick={this.close_no_action}
          style={{
            marginTop: '6px',
            background: retain_armed ? 'var(--state-warning-bg)' : 'var(--surface-card)',
            color: retain_armed ? 'var(--state-warning-text)' : 'var(--text-primary)',
            border: '1px solid var(--border-default)'
          }}
        >
          {retain_armed ? 'Confirm close — no action' : 'Close — no action (retain)'}
        </button>
      </div>
    );
  };

  render_resolve = (item) => {
    // Keep the panel mounted after a successful execute (RESOLVED) or a retain close
    // (NO_ACTION) so the confirmation stays visible after the refresh.
    const {
      resolve_open,
      resolve_loading,
      preview,
      resolve_msg,
      resolve_msg_tone,
      executing,
      exec_result,
      retain_done
    } = this.state;
    if (!can_show_resolve_panel(item, this.props.access_type) && !exec_result && !retain_done) {
      return null;
    }
    return (
      <div style={{ marginTop: '20px', maxWidth: '760px' }}>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '10px',
            marginBottom: '8px'
          }}
        >
          <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
            Resolve lost chargeback (SUPER_USER)
          </div>
          <button type="button" className="ct-small-btn" onClick={this.toggle_resolve}>
            {resolve_open ? 'Hide' : 'Show'}
          </button>
          {resolve_open ? (
            <button
              type="button"
              className="ct-small-btn"
              // H-M5: refreshing mid-execute would swap the token under the running confirm.
              disabled={resolve_loading || executing}
              onClick={() => this.fetch_preview()}
            >
              Refresh preview
            </button>
          ) : null}
        </div>
        {resolve_open ? (
          <div
            style={{
              background: 'var(--surface-card)',
              border: '1px solid var(--border-default)',
              borderRadius: '8px',
              padding: '12px'
            }}
          >
            {resolve_msg ? this.banner(resolve_msg, resolve_msg_tone) : null}
            {resolve_loading ? (
              <div style={{ color: 'var(--text-muted)' }}>Loading preview…</div>
            ) : null}
            {!resolve_loading && !exec_result && preview ? this.render_preview(preview) : null}
            {!resolve_loading && !exec_result && !preview && !resolve_msg ? (
              <div style={{ color: 'var(--text-muted)' }}>No preview loaded.</div>
            ) : null}
            {exec_result ? this.render_exec_result(exec_result) : null}
            {can_show_resolve_panel(item, this.props.access_type) ? this.render_retain() : null}
          </div>
        ) : null}
      </div>
    );
  };

  render_history = (history) => (
    <div style={{ marginTop: '20px', maxWidth: '760px' }}>
      <div style={{ fontWeight: 600, marginBottom: '8px', color: 'var(--text-primary)' }}>
        Audit history
      </div>
      {history.length === 0 ? (
        <div style={{ color: 'var(--text-muted)' }}>No actions recorded.</div>
      ) : (
        history.map((h) => (
          <div
            key={h.id}
            style={{
              padding: '8px 0',
              borderBottom: '1px solid var(--border-default)',
              color: 'var(--text-primary)'
            }}
          >
            <div style={{ fontWeight: 600 }}>
              {h.action}
              {h.from_status || h.to_status ? (
                <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>
                  {' '}
                  {h.from_status || '—'} → {h.to_status || '—'}
                </span>
              ) : null}
            </div>
            <div className="ct-vsm-font" style={{ color: 'var(--text-muted)' }}>
              {h.actor_type}
              {h.cms_user_name ? ` · ${h.cms_user_name}` : ''} · {this.fmt_dt(h.created_date)}
            </div>
            {this.history_detail(h) ? (
              <div
                className="ct-vsm-font"
                style={{ color: 'var(--text-primary)', marginTop: '2px', whiteSpace: 'pre-wrap' }}
              >
                {this.history_detail(h)}
              </div>
            ) : null}
          </div>
        ))
      )}
    </div>
  );

  render() {
    const { loading, item, history } = this.state;
    return (
      <CommonHeader loading={loading} title="Chargeback details">
        <div style={{ textAlign: 'left', marginBottom: '10px' }}>
          <Link
            to="/chargebacks"
            className="link-no-dec"
            style={{ color: 'var(--brand-primary)', fontWeight: 600 }}
          >
            ← Back to Chargebacks
          </Link>
        </div>
        {!loading && !item ? (
          <div style={{ color: 'var(--text-muted)' }}>Chargeback not found.</div>
        ) : null}
        {item ? (
          <>
            {this.render_detail(item)}
            {this.render_resolve(item)}
            {this.render_lifecycle(item)}
            {this.render_history(history)}
          </>
        ) : null}
      </CommonHeader>
    );
  }
}

const mapStateToProps = (state) => ({
  iana_timezone: state.home.iana_timezone,
  access_type: state.home.access_type
});

export default connect(mapStateToProps)(ChargebackDetails);
